import React, { useState, useEffect } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { Record, Equipment, Contact, Vendor, FleetEntry, POC, IpcSearchResult } from '../../types';
import { searchIpc, ApiError } from '../../api';
import { subtractDays, LDG_OFFSET_DAYS } from '../../utils/dates';

interface RecordModalProps {
  active: boolean;
  onClose: () => void;
  onSave: (data: Partial<Record>) => void;
  record?: Record | null;
  vendors: Vendor[];
  fleet: FleetEntry[];
  pocs: POC[];
  isEdit: boolean;
}

const emptyEquipment: Equipment = { position: '', part_number: '', serial_number: '', csn: '', cso: '', drop_dead_date: '' };

const POSITION_PRESETS = ['NLG', 'LH MLG', 'RH MLG'];

interface EquipmentPositionCellProps {
  value: string;
  onChange: (value: string) => void;
}

/** Position picker for an equipment row; selecting 'Other' reveals a free-text field so the
 *  specific equipment name can be typed and stored directly in `position`. */
const EquipmentPositionCell: React.FC<EquipmentPositionCellProps> = ({ value, onChange }) => {
  const [isOther, setIsOther] = useState<boolean>(value !== '' && !POSITION_PRESETS.includes(value));

  // Keep the toggle in sync when the row's value changes externally (e.g. edit load, row removal).
  useEffect(() => {
    if (value !== '' && !POSITION_PRESETS.includes(value)) setIsOther(true);
    else if (POSITION_PRESETS.includes(value)) setIsOther(false);
  }, [value]);

  const handleSelect = (selected: string): void => {
    if (selected === 'Other') {
      setIsOther(true);
      onChange('');
    } else {
      setIsOther(false);
      onChange(selected);
    }
  };

  // In 'Other' mode the cell itself becomes a free-text box (replacing the dropdown) so the
  // typed name overwrites 'Other' in place. Clearing it and leaving reverts to the dropdown.
  if (isOther) {
    return (
      <input
        type="text"
        value={value}
        placeholder="Type position"
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => { if (value === '') setIsOther(false); }}
      />
    );
  }

  return (
    <select value={value} onChange={(e) => handleSelect(e.target.value)}>
      <option value="">Select…</option>
      {POSITION_PRESETS.map((p) => (
        <option key={p} value={p}>{p}</option>
      ))}
      <option value="Other">Other</option>
    </select>
  );
};

const RecordModal: React.FC<RecordModalProps> = ({ active, onClose, onSave, record, vendors, fleet, pocs, isEdit }) => {
  const [carrier, setCarrier] = useState('');
  const [tail, setTail] = useState('');
  const [aircraftType, setAircraftType] = useState('');
  const [inductionDate, setInductionDate] = useState('');
  const [ldgInductionDate, setLdgInductionDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [checkType, setCheckType] = useState('');
  const [status, setStatus] = useState('');
  const [primaryTask, setPrimaryTask] = useState('');
  const [dropDeadDate, setDropDeadDate] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poIssuedDate, setPoIssuedDate] = useState('');
  const [mxMro, setMxMro] = useState('');
  const [lgVendor, setLgVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [equipment, setEquipment] = useState<Equipment[]>([{ ...emptyEquipment }]);
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    if (record && isEdit) {
      setCarrier(record.carrier || '');
      setTail(record.tail || '');
      setAircraftType(record.aircraft_type || '');
      setLdgInductionDate(record.ldg_induction_date || '');
      setInductionDate(record.induction_date || '');
      setDeliveryDate(record.delivery_date || '');
      setCheckType(record.check_type || '');
      setStatus(record.status || '');
      setPrimaryTask(record.primary_task || '');
      setDropDeadDate(record.lg_drop_dead_date || '');
      setPoNumber(record.po_number || '');
      setPoIssuedDate(record.po_issued_date || '');
      setMxMro(record.mx_145_mro || '');
      setLgVendor(record.landing_gear_vendor || '');
      setNotes(record.notes || '');
      setEquipment(record.equipment?.length ? record.equipment : [{ ...emptyEquipment }]);
      setContacts(record.contacts || []);
    } else if (!isEdit) {
      setCarrier(''); setTail(''); setAircraftType('');
      setLdgInductionDate('');
      setInductionDate(''); setDeliveryDate('');
      setCheckType(''); setStatus('');
      setPrimaryTask(''); setDropDeadDate('');
      setPoNumber(''); setPoIssuedDate('');
      setMxMro(''); setLgVendor(''); setNotes('');
      setEquipment([{ ...emptyEquipment }]);
      setContacts([]);
    }
  }, [record, isEdit, active]);

  // Fleet cascading
  const carriers = Array.from(new Set(fleet.map((f) => f.carrier))).sort();
  const tails = fleet.filter((f) => f.carrier === carrier).map((f) => f.tail_num);

  const handleCarrierChange = (c: string) => {
    setCarrier(c);
    setTail('');
    setAircraftType('');
  };

  const handleTailChange = (t: string) => {
    setTail(t);
    const match = fleet.find((f) => f.carrier === carrier && f.tail_num === t);
    setAircraftType(match?.aircraft_frame || '');
  };

  // Equipment
  const updateEquipment = (index: number, field: keyof Equipment, value: string) => {
    const updated = [...equipment];
    updated[index] = { ...updated[index], [field]: value };
    setEquipment(updated);
    // Auto-update main drop dead date
    const dates = updated.map((e) => e.drop_dead_date).filter(Boolean).sort();
    setDropDeadDate(dates[0] || '');
  };

  const addEquipment = () => setEquipment([...equipment, { ...emptyEquipment }]);
  const removeEquipment = (index: number) => {
    const updated = equipment.filter((_, i) => i !== index);
    const final = updated.length ? updated : [{ ...emptyEquipment }];
    setEquipment(final);
    // Recalculate drop dead date
    const dates = final.map((e) => e.drop_dead_date).filter(Boolean).sort();
    setDropDeadDate(dates[0] || '');
  };

  // Recalculate main drop dead date whenever equipment changes
  useEffect(() => {
    const dates = equipment.map((e) => e.drop_dead_date).filter(Boolean).sort();
    setDropDeadDate(dates[0] || '');
  }, [equipment]);

  // LDG Delivery Date is derived: 10 days before the aircraft induction date.
  // (LDG Induction Date is entered manually and loaded from the record as-is.)
  useEffect(() => {
    setDeliveryDate(subtractDays(inductionDate, LDG_OFFSET_DAYS));
  }, [inductionDate]);

  // POC picker state
  const [pocPickerOpen, setPocPickerOpen] = useState(false);
  const [pickerPocId, setPickerPocId] = useState('');

  // IPC lookup state: which equipment row is open, and its results/loading/error.
  const [ipcRow, setIpcRow] = useState<number | null>(null);
  const [ipcLoading, setIpcLoading] = useState(false);
  const [ipcError, setIpcError] = useState('');
  const [ipcResults, setIpcResults] = useState<IpcSearchResult[]>([]);
  const [ipcSelected, setIpcSelected] = useState(0);

  const openIpcLookup = async (rowIndex: number): Promise<void> => {
    const partNumber = equipment[rowIndex]?.part_number?.trim() || '';
    setIpcRow(rowIndex);
    setIpcResults([]);
    setIpcSelected(0);
    setIpcError('');
    if (!carrier) {
      setIpcError('Select a carrier on the record before looking up a part number.');
      return;
    }
    if (!partNumber) {
      setIpcError('Enter a part number in this row before looking it up.');
      return;
    }
    setIpcLoading(true);
    try {
      const response = await searchIpc(carrier, partNumber);
      setIpcResults(response.results);
      setIpcSelected(0);
      if (response.results.length === 0) setIpcError('No matching IPC pages found.');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setIpcError(`Lookup failed: ${message}`);
    } finally {
      setIpcLoading(false);
    }
  };

  const closeIpcLookup = (): void => {
    setIpcRow(null);
    setIpcResults([]);
    setIpcError('');
  };

  // The POCs offered for a record are those belonging to the record's own vendors: the
  // Component Maintenance vendor and the 145-MRO already selected on this record.
  const recordVendorNames = [lgVendor, mxMro].filter(Boolean);
  const availablePocs = pocs.filter((p) => recordVendorNames.includes(p.vendor));

  const handleAddPocFromPicker = () => {
    const selectedPoc = pocs.find((p) => String(p.id) === pickerPocId);
    if (selectedPoc) {
      setContacts([...contacts, { poc_name: selectedPoc.poc_name, vendor: selectedPoc.vendor }]);
    }
    setPocPickerOpen(false);
    setPickerPocId('');
  };

  // Vendor dropdowns
  const mroVendors = vendors.filter((v) => v.vendor_type === '145-MRO');
  const cmVendors = vendors.filter((v) => v.vendor_type === 'Component Maintenance');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      carrier, tail, aircraft_type: aircraftType,
      ldg_induction_date: ldgInductionDate,
      induction_date: inductionDate, delivery_date: deliveryDate,
      check_type: checkType, status,
      primary_task: primaryTask, lg_drop_dead_date: dropDeadDate,
      po_number: poNumber, po_issued_date: poIssuedDate,
      mx_145_mro: mxMro, landing_gear_vendor: lgVendor,
      notes, equipment, contacts,
    });
  };

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Record' : 'New Record'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-header-row">
            <div className="form-group">
              <label>Carrier</label>
              {isEdit ? (
                <input type="text" value={carrier} readOnly tabIndex={-1} />
              ) : (
                <select value={carrier} onChange={(e) => handleCarrierChange(e.target.value)} required>
                  <option value="">Select…</option>
                  {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Tail #</label>
              {isEdit ? (
                <input type="text" value={tail} readOnly tabIndex={-1} />
              ) : (
                <select value={tail} onChange={(e) => handleTailChange(e.target.value)} required>
                  <option value="">Select carrier first…</option>
                  {tails.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Aircraft Type</label>
              <input type="text" value={aircraftType} readOnly tabIndex={-1} />
            </div>
            <div className="form-group">
              <label>LDG Induction Date</label>
              <input type="date" value={ldgInductionDate} onChange={(e) => setLdgInductionDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>LDG Delivery Date</label>
              <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>A/C Induction Date</label>
              <input type="date" value={inductionDate} onChange={(e) => setInductionDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Check Type</label>
              <select value={checkType} onChange={(e) => setCheckType(e.target.value)} required>
                <option value="">Select…</option>
                {['A-Check', 'B-Check', 'C-Check', 'D-Check', 'Stand Alone', 'Unknown'].map((ct) => (
                  <option key={ct} value={ct}>{ct}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} required>
                <option value="">Select…</option>
                {['Complete', 'In Work', 'In Transit', 'In Planning', 'Scheduled', 'On Hold'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Primary Task</label>
              <input type="text" value={primaryTask} onChange={(e) => setPrimaryTask(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Drop Dead Date</label>
              <input type="date" value={dropDeadDate} readOnly tabIndex={-1} />
            </div>
            <div className="form-group">
              <label>PO Number</label>
              <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
            </div>
            <div className="form-group">
              <label>PO Issued Date</label>
              <input type="date" value={poIssuedDate} onChange={(e) => setPoIssuedDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Mx 145-MRO</label>
              <select value={mxMro} onChange={(e) => setMxMro(e.target.value)}>
                <option value="">Select…</option>
                {mroVendors.map((v) => <option key={v.id} value={v.vendor_name}>{v.vendor_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Component Maintenance Vendor</label>
              <select value={lgVendor} onChange={(e) => setLgVendor(e.target.value)}>
                <option value="">Select…</option>
                {cmVendors.map((v) => <option key={v.id} value={v.vendor_name}>{v.vendor_name}</option>)}
              </select>
            </div>
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {/* Equipment sub-table */}
          <div className="subtable-section">
            <div className="subtable-header">
              <h3>Equipment</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addEquipment}>+ Add Equipment</button>
            </div>
            <table className="subtable">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Part Number</th>
                  <th>Serial Number</th>
                  <th>CSN</th>
                  <th>CSO</th>
                  <th>Drop Dead Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {equipment.map((eq, i) => (
                  <tr key={i}>
                    <td>
                      <EquipmentPositionCell value={eq.position} onChange={(v) => updateEquipment(i, 'position', v)} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                        <input type="text" value={eq.part_number} onChange={(e) => updateEquipment(i, 'part_number', e.target.value)} />
                        <button
                          type="button"
                          className="btn-icon"
                          title="Look up this part number in the IPC"
                          aria-label="Look up part number in the IPC"
                          onClick={() => openIpcLookup(i)}
                        >&#128269;</button>
                      </div>
                    </td>
                    <td><input type="text" value={eq.serial_number} onChange={(e) => updateEquipment(i, 'serial_number', e.target.value)} /></td>
                    <td><input type="number" value={eq.csn} onChange={(e) => updateEquipment(i, 'csn', e.target.value)} /></td>
                    <td><input type="number" value={eq.cso} onChange={(e) => updateEquipment(i, 'cso', e.target.value)} /></td>
                    <td><input type="date" value={eq.drop_dead_date} onChange={(e) => updateEquipment(i, 'drop_dead_date', e.target.value)} /></td>
                    <td><button type="button" className="btn-icon delete" onClick={() => removeEquipment(i)}>&#128465;</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* IPC lookup popup: renders the matched PDF inline (no new tab). */}
          {ipcRow !== null && (
            <div className="modal-overlay active" style={{ zIndex: 120 }} onClick={(e) => { if (e.target === e.currentTarget) closeIpcLookup(); }}>
              <div className="modal" style={{ width: 'min(1000px, 92vw)', maxWidth: '92vw' }}>
                <div className="modal-header">
                  <h2>IPC Lookup{carrier ? ` — ${carrier}` : ''}</h2>
                  <button className="modal-close" type="button" onClick={closeIpcLookup}>&times;</button>
                </div>
                <div className="modal-body">
                  {ipcLoading && <p>Searching the illustrated parts catalog…</p>}
                  {!ipcLoading && ipcError && <p style={{ color: 'var(--text-dim)' }}>{ipcError}</p>}
                  {!ipcLoading && !ipcError && ipcResults.length > 0 && (
                    <>
                      {/* When several documents match, tabs switch which PDF is shown. */}
                      {ipcResults.length > 1 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                          {ipcResults.map((result, idx) => (
                            <button
                              key={result.uri}
                              type="button"
                              className={`btn btn-sm ${idx === ipcSelected ? 'btn-primary' : 'btn-secondary'}`}
                              title={result.title}
                              onClick={() => setIpcSelected(idx)}
                            >{result.title}</button>
                          ))}
                        </div>
                      )}
                      <object
                        data={ipcResults[ipcSelected].url}
                        type="application/pdf"
                        width="100%"
                        style={{ height: '72vh', border: 'none' }}
                        aria-label={`IPC document: ${ipcResults[ipcSelected].title}`}
                      >
                        <p style={{ color: 'var(--text-dim)' }}>
                          This PDF can&apos;t be displayed inline.{' '}
                          <a href={ipcResults[ipcSelected].url} target="_blank" rel="noopener noreferrer">
                            Open {ipcResults[ipcSelected].title} in a new tab
                          </a>.
                        </p>
                      </object>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Contacts sub-table */}
          <div className="subtable-section">
            <div className="subtable-header">
              <h3>Point of Contact</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPocPickerOpen(true)}>+</button>
            </div>
            <table className="subtable">
              <thead>
                <tr>
                  <th>POC Name</th>
                  <th>Vendor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '0.5rem' }}>No contacts</td>
                  </tr>
                ) : (
                  contacts.map((c, i) => (
                    <tr key={i}>
                      <td>{c.poc_name}</td>
                      <td>{c.vendor}</td>
                      <td>
                        <button type="button" className="btn-icon delete" onClick={() => setContacts(contacts.filter((_, j) => j !== i))}>&#128465;</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* POC Picker Popup */}
          {pocPickerOpen && (
            <div className="modal-overlay active" style={{ zIndex: 120 }} onClick={(e) => { if (e.target === e.currentTarget) setPocPickerOpen(false); }}>
              <div className="modal modal-narrow">
                <div className="modal-header">
                  <h2>Add Point of Contact</h2>
                  <button className="modal-close" onClick={() => setPocPickerOpen(false)}>&times;</button>
                </div>
                <div className="poc-fields">
                  {recordVendorNames.length === 0 ? (
                    <p style={{ color: 'var(--text-dim)', padding: '0.5rem 0' }}>
                      Select a Component Maintenance Vendor or 145-MRO on the record first.
                    </p>
                  ) : (
                    <div className="form-group">
                      <label>POC</label>
                      <select value={pickerPocId} onChange={(e) => setPickerPocId(e.target.value)}>
                        <option value="">Select…</option>
                        {availablePocs.map((p) => (
                          <option key={p.id} value={String(p.id)}>{p.poc_name} — {p.vendor}</option>
                        ))}
                      </select>
                      {availablePocs.length === 0 && (
                        <p style={{ color: 'var(--text-dim)', marginTop: '0.4rem' }}>
                          No POCs found for {recordVendorNames.join(' or ')}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setPocPickerOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleAddPocFromPicker} disabled={!pickerPocId}>Add</button>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{isEdit ? 'Update Record' : 'Create Record'}</button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
};

export default RecordModal;
